// useDeskJobs — the generation jobs started from the Studio desk, followed
// live until they end, so the desk can say where each stands before the
// library does (reading the sources first, or a read that saved no fact).
// Shell-side: the feed subscription is the impurity; the desk gets the
// snapshots (deskFacts.deskNotes). View state: a reload forgets them.

export { useDeskJobs };

import { useEffect, useState } from "react";
import type { GenerationJobSnapshot } from "../ports/generation.js";
import type { AppPorts } from "../ports.js";

// Followed until it stops on its own (`paused_budget` waits for a person).
const live = (job: GenerationJobSnapshot): boolean => {
    switch (job.phase) {
        case "reading":
        case "running":
        case "stopping":
        case "paused_budget":
            return true;
        case "stopped":
        case "done":
        case "read_failed":
        case "interrupted":
            return false;
        default:
            return false;
    }
};

const useDeskJobs = (
    ports: AppPorts,
): {
    readonly jobs: ReadonlyMap<string, GenerationJobSnapshot>;
    // A job a desk command just started (its first snapshot).
    readonly follow: (job: GenerationJobSnapshot) => void;
} => {
    const [jobs, setJobs] = useState<
        ReadonlyMap<string, GenerationJobSnapshot>
    >(new Map());
    const keep = (job: GenerationJobSnapshot) =>
        setJobs((m) => new Map(m).set(job.root, job));
    const roots = [...jobs.values()]
        .filter(live)
        .map((j) => j.root)
        .sort()
        .join(" ");
    useEffect(() => {
        const closes = (roots === "" ? [] : roots.split(" ")).map((root) =>
            ports.watchGenerationJob(root, (event) => {
                if (event.type !== "generation_feed_snapshot") return;
                const job = event.snapshot;
                setJobs((m) => new Map(m).set(job.root, job));
            }),
        );
        return () => {
            for (const close of closes) close();
        };
    }, [ports, roots]);
    return { jobs, follow: keep };
};
